import styled from '@emotion/styled';
import { useState } from 'react';

import { IconButton } from '@travelnest/hui';

import { Stack } from '@/components/ui/Stack';
import { Text } from '@/components/ui/Text';
import { DialogDrawer } from '@/components/ui/dialog-drawer';
import { colors, font, spacing } from '@/lib/theme';
import { useI18n } from '@/locales';

import type { StayGuests } from './useStayPricing';

const Row = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${spacing.space40};
	padding: ${spacing.space30} 0;

	& + & {
		border-top: 1px solid ${colors.interface.border};
	}
`;

const Counter = styled.div`
	display: flex;
	align-items: center;
	gap: ${spacing.space30};
`;

const Count = styled.output`
	min-width: 2ch;
	text-align: center;
	${font('IM110')}
	color: ${colors.typography.heading};
	font-variant-numeric: tabular-nums;
`;

interface CounterRowProps {
	id: string;
	label: string;
	hint: string;
	value: number;
	min: number;
	max: number;
	onChange: (value: number) => void;
}

const CounterRow = ({
	id,
	label,
	hint,
	value,
	min,
	max,
	onChange,
}: CounterRowProps) => {
	const t = useI18n();
	return (
		<Row>
			<div>
				<Text id={id} color="heading" weight="medium">
					{label}
				</Text>
				<Text variant="bodySmall">{hint}</Text>
			</div>
			<Counter role="group" aria-labelledby={id}>
				<IconButton
					icon="Minus"
					variant="secondary"
					fill="light"
					size="small"
					aria-label={`${t('common.showLess')} ${label}`}
					disabled={value <= min}
					onClick={() => onChange(Math.max(min, value - 1))}
				/>
				<Count aria-live="polite">{value}</Count>
				<IconButton
					icon="Plus"
					variant="secondary"
					fill="light"
					size="small"
					aria-label={`${t('common.showMore')} ${label}`}
					disabled={value >= max}
					onClick={() => onChange(Math.min(max, value + 1))}
				/>
			</Counter>
		</Row>
	);
};

interface GuestPickerProps {
	open: boolean;
	onClose: () => void;
	maxGuests: number;
	/** Pets the guest can bring at a fee; 0 or unset hides the row. */
	maxPets?: number;
	guests: StayGuests;
	onChange: (guests: StayGuests) => void;
}

/**
 * Lets the guest choose adults and children within the property's capacity.
 * Changes are held in a draft until "Save", so cancelling leaves the stay as
 * it was. The draft restarts from the current stay each time the picker opens.
 */
export const GuestPicker = ({
	open,
	onClose,
	maxGuests,
	maxPets = 0,
	guests,
	onChange,
}: GuestPickerProps) => {
	const t = useI18n();
	const [draft, setDraft] = useState<StayGuests>(guests);
	const [wasOpen, setWasOpen] = useState(open);
	if (open !== wasOpen) {
		setWasOpen(open);
		if (open) setDraft(guests);
	}

	const save = () => {
		onChange(draft);
		onClose();
	};

	return (
		<DialogDrawer
			open={open}
			onOpenChange={(next) => !next && onClose()}
			title={t('booking.guestsPicker.title')}
			closeLabel={t('common.close')}
			desktopWidth="440px"
			cancelButton={{ text: t('common.cancel'), onClick: onClose }}
			actionButton={{ text: t('booking.save'), onClick: save }}
		>
			<Stack gap="space20">
				<Text variant="bodySmall">
					{t('booking.guestsPicker.max', { count: maxGuests })}
				</Text>
				<div>
					<CounterRow
						id="guest-picker-adults"
						label={t('booking.guestsPicker.adults')}
						hint={t('booking.guestsPicker.adultsHint')}
						value={draft.adults}
						min={1}
						max={maxGuests - draft.children}
						onChange={(adults) =>
							setDraft((d) => ({ ...d, adults }))
						}
					/>
					<CounterRow
						id="guest-picker-children"
						label={t('booking.guestsPicker.children')}
						hint={t('booking.guestsPicker.childrenHint')}
						value={draft.children}
						min={0}
						max={maxGuests - draft.adults}
						onChange={(children) =>
							setDraft((d) => ({ ...d, children }))
						}
					/>
					{maxPets > 0 && (
						<CounterRow
							id="guest-picker-pets"
							label={t('booking.guestsPicker.pets')}
							hint={t('booking.guestsPicker.petsHint', {
								count: maxPets,
							})}
							value={draft.pets ?? 0}
							min={0}
							max={maxPets}
							onChange={(pets) =>
								setDraft((d) => ({ ...d, pets }))
							}
						/>
					)}
				</div>
			</Stack>
		</DialogDrawer>
	);
};
